"use client";

import { Quote, Star } from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";

interface ReviewCardProps {
  name: string;
  avatar: string; // Keep for backward compatibility but won't be used
  rating: number;
  review: string;
  date: string;
  ratingsNumber?: number; // Optional prop for number of stars, default is 5
}

export default function ReviewCard({
  name,
  avatar,
  rating,
  review,
  date,
  ratingsNumber = 5, // Default to 5 stars if not provided
}: ReviewCardProps) {
  const t = useTranslations("general");
  const locale = useLocale();
  
  // Function to convert numbers to Arabic numerals if locale is Arabic
  const localizeNumber = (num: number) => {
    if (locale === "ar") {
      return num.toLocaleString("ar-EG");
    }
    return num.toString();
  };

  // Function to get initials from name
  const getInitials = (fullName: string) => {
    return fullName[0].toUpperCase();
  };

  // Function to get a consistent color based on name
  const getAvatarColor = (fullName: string) => {
    const colors = [
      'bg-primary',
      'bg-purple-500', 
      'bg-purple-600',
      'bg-purple-400',
      'bg-violet-500',
      'bg-violet-600',
      'bg-fuchsia-500',
      'bg-indigo-500'
    ];
    const index = fullName.charCodeAt(0) % colors.length;
    return colors[index];
  };
  
  return (
    <Card className="bg-white shadow-lg border-0 rounded-2xl overflow-hidden max-sm:mx-2 h-full flex flex-col">
      <CardHeader className="text-start p-4">
        <Quote className="fill-black w-6 h-6" />
      </CardHeader>
      
      <CardContent className="p-4 font-poppins flex-1">
        <p className="text-gray-700 text-sm leading-relaxed font-poppins min-h-[80px] flex items-start">
          {review}
        </p>
      </CardContent>
      
      <CardFooter className="flex items-center justify-between p-4 border-t mt-auto">
        <div className="flex items-center gap-3">
          <div className={`relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-gray-100 flex-shrink-0 flex items-center justify-center ${getAvatarColor(name)}`}>
            <span className="text-white font-bold text-lg">{getInitials(name)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm truncate">{name}</h3>
            <p className="text-xs text-gray-500 font-poppins">{date}</p>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          {/* Star rating */}
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${
                  i < rating
                    ? "fill-amber-400 text-amber-400"
                    : "fill-gray-200 text-gray-200"
                }`}
              />
            ))}
          </div>
          
          {/* Rating count */}
          <p className="text-gray-500 text-xs font-poppins">
            ({localizeNumber(ratingsNumber)}) {t("ratings")}
          </p>
        </div>
      </CardFooter>
    </Card>
  );
}
